import React, { useState, useEffect } from 'react';
import { journeyService } from '../services/journeyService';
import { jobService } from '../services/jobService';

const nowDateTimeLocal = () => {
  const now = new Date();
  now.setSeconds(0, 0);
  return now.toISOString().slice(0, 16);
};

const JourneyModal = ({ journey, prefill, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    job_id: '',
    journey_reason_id: '',
    start_postcode: '',
    end_postcode: '',
    mileage: '',
    started_at: nowDateTimeLocal(),
  });
  const [jobs, setJobs] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const isEditing = !!journey;

  useEffect(() => {
    jobService.getJobs()
      .then(res => setJobs((res.data || []).slice().sort((a, b) => a.title.localeCompare(b.title))))
      .catch(() => {});
    journeyService.getReasons()
      .then(res => setReasons(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (journey) {
      setFormData({
        job_id: journey.job_id || '',
        journey_reason_id: journey.journey_reason_id || '',
        start_postcode: journey.start_postcode || '',
        end_postcode: journey.end_postcode || '',
        mileage: journey.mileage || '',
        started_at: journey.started_at ? journey.started_at.slice(0, 16) : nowDateTimeLocal(),
      });
    }
  }, [journey]);

  useEffect(() => {
    if (!journey && prefill && reasons.length > 0) {
      const resolvedReasonId = prefill.journey_reason_name
        ? (reasons.find(r => r.name === prefill.journey_reason_name)?.id || '')
        : (prefill.journey_reason_id || '');
      setFormData(prev => ({
        ...prev,
        job_id: prefill.job_id || '',
        journey_reason_id: resolvedReasonId,
        start_postcode: prefill.start_postcode || '',
        end_postcode: prefill.end_postcode || '',
        mileage: prefill.mileage || '',
        started_at: prefill.started_at || nowDateTimeLocal(),
      }));
    }
  }, [journey, prefill, reasons]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };

      // Auto-fill postcodes based on the selected reason and job's site postcode
      if (name === 'journey_reason_id' || name === 'job_id') {
        const reasonId = name === 'journey_reason_id' ? value : prev.journey_reason_id;
        const jobId = name === 'job_id' ? value : prev.job_id;
        const selectedReason = reasons.find(r => String(r.id) === String(reasonId));
        const selectedJob = jobs.find(j => String(j.id) === String(jobId));
        const sitePostcode = selectedJob?.site?.postcode || '';
        if (selectedReason?.name === 'Site Visit') {
          updated.end_postcode = sitePostcode || updated.end_postcode;
        } else if (selectedReason?.name === 'Return to office' || selectedReason?.name === 'Return home') {
          updated.start_postcode = sitePostcode || updated.start_postcode;
        }
      }

      return updated;
    });
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      await onSave(formData);
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: error.response?.data?.message || 'An error occurred. Please try again.' });
      }
    }

    setLoading(false);
  };

  const jobSelected = !!formData.job_id;
  const reasonSelected = !!formData.journey_reason_id;
  const detailsUnlocked = jobSelected && reasonSelected;

  const inputClass = (field, disabled = false) =>
    `w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-300 focus:outline-none ${
      disabled
        ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 cursor-not-allowed'
        : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
    } ${
      !disabled && errors[field]
        ? 'border-red-500 focus:border-red-500'
        : !disabled
        ? 'border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400'
        : ''
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {isEditing ? 'Edit Journey' : 'Add New Journey'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl font-semibold leading-none transition-colors"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {errors.general && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-300 text-sm">
              {errors.general}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Job <span className="text-red-500">*</span>
            </label>
            <select
              name="job_id"
              value={formData.job_id}
              onChange={handleChange}
              className={inputClass('job_id')}
            >
              <option value="">Select a job...</option>
              {jobs.map(job => (
                <option key={job.id} value={job.id}>{job.title}</option>
              ))}
            </select>
            {errors.job_id && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.job_id[0]}</p>}
          </div>

          <div className={!jobSelected ? 'opacity-40 pointer-events-none' : ''}>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Reason <span className="text-red-500">*</span>
            </label>
            <select
              name="journey_reason_id"
              value={formData.journey_reason_id}
              onChange={handleChange}
              disabled={!jobSelected}
              className={inputClass('journey_reason_id', !jobSelected)}
            >
              <option value="">Select a reason...</option>
              {reasons.map(reason => (
                <option key={reason.id} value={reason.id}>{reason.name}</option>
              ))}
            </select>
            {errors.journey_reason_id && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.journey_reason_id[0]}</p>}
          </div>

          <div className={!detailsUnlocked ? 'opacity-40 pointer-events-none' : ''}>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Start Date &amp; Time
            </label>
            <input
              type="datetime-local"
              name="started_at"
              value={formData.started_at}
              onChange={handleChange}
              disabled={!detailsUnlocked}
              className={inputClass('started_at', !detailsUnlocked)}
            />
            {errors.started_at && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.started_at[0]}</p>}
          </div>

          <div className={!detailsUnlocked ? 'opacity-40 pointer-events-none' : ''}>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Start Post Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="start_postcode"
              value={formData.start_postcode}
              onChange={handleChange}
              disabled={!detailsUnlocked}
              className={inputClass('start_postcode', !detailsUnlocked)}
              placeholder="e.g. SW1A 1AA"
            />
            {errors.start_postcode && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.start_postcode[0]}</p>}
          </div>

          <div className={!detailsUnlocked ? 'opacity-40 pointer-events-none' : ''}>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              End Post Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="end_postcode"
              value={formData.end_postcode}
              onChange={handleChange}
              disabled={!detailsUnlocked}
              className={inputClass('end_postcode', !detailsUnlocked)}
              placeholder="e.g. EC1A 1BB"
            />
            {errors.end_postcode && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.end_postcode[0]}</p>}
          </div>

          <div className={!detailsUnlocked ? 'opacity-40 pointer-events-none' : ''}>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Mileage <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="mileage"
              value={formData.mileage}
              onChange={handleChange}
              min="0"
              step="0.1"
              disabled={!detailsUnlocked}
              className={inputClass('mileage', !detailsUnlocked)}
              placeholder="e.g. 12.5"
            />
            {errors.mileage && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.mileage[0]}</p>}
          </div>

          {formData.start_postcode && formData.end_postcode && (
            <a
              href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(formData.start_postcode)}&destination=${encodeURIComponent(formData.end_postcode)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full px-4 py-2.5 border-2 border-green-500 text-green-600 dark:text-green-400 dark:border-green-500 rounded-lg font-semibold hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
            >
              <span>🗺️</span>
              View in Google Maps
            </a>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {loading ? 'Saving...' : (isEditing ? 'Update Journey' : 'Add Journey')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JourneyModal;
